/**
 * React Query hooks for admin Stripe product & price management.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminListStripeProducts,
  adminUpdateStripeProduct,
  adminUpdateStripePrice,
  adminCreateStripeProduct,
  adminCreateStripePrice,
  type AdminStripeProductsResponse,
  type AdminUpdateStripeProductResponse,
  type AdminUpdateStripePriceResponse,
  type AdminCreateStripeProductResponse,
  type AdminCreateStripePriceResponse,
} from "../services/naraDoor";

const STRIPE_PRODUCTS_KEY = ["adminStripeProducts"];

export function useAdminStripeProducts() {
  return useQuery<AdminStripeProductsResponse>({
    queryKey: STRIPE_PRODUCTS_KEY,
    queryFn: () => adminListStripeProducts(),
  });
}

export function useAdminUpdateStripeProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminUpdateStripeProductResponse,
    Error,
    { productId: string; name?: string; description?: string; active?: boolean }
  >({
    mutationFn: ({ productId, ...updates }) =>
      adminUpdateStripeProduct(productId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRIPE_PRODUCTS_KEY });
    },
  });
}

export function useAdminUpdateStripePrice() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminUpdateStripePriceResponse,
    Error,
    {
      priceId: string;
      active?: boolean;
      boosts?: number;
      showOnPricing?: boolean;
      displayName?: string;
      displayDescription?: string;
      popular?: boolean;
      sortOrder?: number;
    }
  >({
    mutationFn: ({ priceId, ...updates }) =>
      adminUpdateStripePrice(priceId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRIPE_PRODUCTS_KEY });
    },
  });
}

export function useAdminCreateStripeProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminCreateStripeProductResponse,
    Error,
    { name: string; description?: string }
  >({
    mutationFn: ({ name, description }) =>
      adminCreateStripeProduct(name, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRIPE_PRODUCTS_KEY });
    },
  });
}

export function useAdminCreateStripePrice() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminCreateStripePriceResponse,
    Error,
    {
      productId: string;
      unitAmount: number;
      currency: string;
      type: "one_time" | "recurring";
      boosts: number;
      interval?: "month" | "year";
    }
  >({
    mutationFn: ({ productId, unitAmount, currency, type, boosts, interval }) =>
      adminCreateStripePrice(productId, unitAmount, currency, type, boosts, interval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRIPE_PRODUCTS_KEY });
    },
  });
}
